/**
 * RESPONSIBILITY: bring a named region of the page into view once it has been drawn.
 *
 * `#about/believe` is an address for a PART of the About page: the menu's "What we
 * believe" points there. The region is found by `data-anchor`, so any band can be given a
 * name and linked to without this knowing what it is.
 *
 * WAITS, TWICE OVER. The sections arrive from the server after the first render, so the
 * region may not exist yet; and useScrollTop, which runs in the same commit, puts the page
 * back at the top when the address changes. So this looks on the next frame and every
 * frame after until the region appears — and gives up quietly, leaving the reader at the
 * top of the page, if it never does.
 */
import { useEffect } from 'react';

const GIVE_UP_MS = 4000;

export function useScrollToAnchor(name: string | undefined): void {
  useEffect(() => {
    if (!name) return;
    const start = performance.now();
    let frame = 0;

    const seek = () => {
      const el = document.querySelector<HTMLElement>(`[data-anchor="${CSS.escape(name)}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      if (performance.now() - start < GIVE_UP_MS) frame = requestAnimationFrame(seek);
    };

    frame = requestAnimationFrame(seek);
    return () => cancelAnimationFrame(frame);
  }, [name]);
}
